import React, { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Bold,
  Calendar,
  ChevronRight,
  Check,
  Circle,
  Diamond,
  Flag,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Paperclip,
  Search,
  Strikethrough,
  Underline,
  UserRound,
  X,
} from 'lucide-react'
import { projectsApi } from '../../api/projects'
import { useAuthStore } from '../../store/authStore'
import { Modal } from '../common/Modal'
import { Button } from '../common/Button'
import {
  ALLOWED_ATTACHMENT_ACCEPT,
  getAllowedAttachmentMessage,
  isAllowedAttachmentFile,
} from '../../lib/attachments'
import {
  createInlineMediaDraft,
  isInlineMediaFile,
  descriptionToEditableHtml,
  type InlineMediaDraft,
} from '../../lib/issueDescription'
import { WORK_TYPE_OPTIONS, formatWorkType } from '../../lib/workTypes'
import type { IssueStatus, Issue } from '../../types'

export interface CreateIssuePayload {
  title: string
  description?: string
  statusId: string
  priority: Issue['priority']
  assigneeIds?: string[]
  assigneeEstimates?: {
    userId: string
    estimatedHours?: number | null
    workType?: (typeof WORK_TYPE_OPTIONS)[number]['value'] | null
    dueDate?: string | null
  }[]
  workEstimates?: { workType: (typeof WORK_TYPE_OPTIONS)[number]['value']; hours: number }[]
  labelIds?: string[]
  milestoneId?: string
  componentId?: string
  estimatedHours?: number
  startDate?: string
  dueDate?: string
  attachments?: File[]
  inlineMedia?: InlineMediaDraft[]
}

interface IssueFormProps {
  isOpen: boolean
  onClose: () => void
  projectId: string
  statuses: IssueStatus[]
  defaultStatusId?: string
  initialData?: Partial<Issue>
  onSubmit: (data: CreateIssuePayload) => void
  beforeSubmit?: (data: CreateIssuePayload) => boolean
  isSubmitting?: boolean
  title?: string
}

const PRIORITY_OPTIONS = [
  { value: 'NO_PRIORITY', label: 'No priority' },
  { value: 'URGENT', label: 'Urgent' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
] as const

const resolveDefaultStatusId = (statuses: IssueStatus[], defaultStatusId?: string) =>
  defaultStatusId
  || statuses.find((status) => status.name.toLowerCase() === 'backlog')?.id
  || statuses.find((status) => status.category === 'TODO')?.id
  || statuses[0]?.id
  || ''

const createInitialFormState = (initialData: Partial<Issue> | undefined, defaultStatusId: string) => ({
  title: initialData?.title || '',
  description: initialData?.description || '',
  statusId: initialData?.statusId || defaultStatusId,
  priority: (initialData?.priority || 'NO_PRIORITY') as Issue['priority'],
  assigneeIds: initialData?.assignees?.map((assignee) => assignee.user.id) || ([] as string[]),
  assigneeEstimates: Object.fromEntries(
    (initialData?.assignees || []).map((assignee) => [
      assignee.user.id,
      assignee.estimatedHours?.toString() || '',
    ])
  ) as Record<string, string>,
  assigneeWorkTypes: Object.fromEntries(
    (initialData?.assignees || []).map((assignee) => [
      assignee.user.id,
      assignee.workType || '',
    ])
  ) as Record<string, (typeof WORK_TYPE_OPTIONS)[number]['value'] | ''>,
  assigneeDueDates: Object.fromEntries(
    (initialData?.assignees || []).map((assignee) => [
      assignee.user.id,
      assignee.dueDate ? assignee.dueDate.split('T')[0] : '',
    ])
  ) as Record<string, string>,
  labelIds: initialData?.labels?.map((label) => label.label.id) || ([] as string[]),
  milestoneId: initialData?.milestoneId || '',
  componentId: initialData?.componentId || '',
  estimatedHours: initialData?.estimatedHours?.toString() || '',
  taskWorkType: (initialData?.workEstimates?.find((estimate) => estimate.hours > 0)?.workType
    || 'FRONTEND_DEVELOPMENT') as (typeof WORK_TYPE_OPTIONS)[number]['value'],
  workEstimates: Object.fromEntries(
    WORK_TYPE_OPTIONS.map((option) => [
      option.value,
      initialData?.workEstimates?.find((estimate) => estimate.workType === option.value)?.hours?.toString() || '',
    ])
  ) as Record<(typeof WORK_TYPE_OPTIONS)[number]['value'], string>,
  startDate: initialData?.startDate ? initialData.startDate.split('T')[0] : '',
  dueDate: initialData?.dueDate ? initialData.dueDate.split('T')[0] : '',
})

const hasWorkEstimateValues = (initialData?: Partial<Issue>) =>
  (initialData?.workEstimates || []).some((estimate) => estimate.hours > 0)

export default function IssueForm({
  isOpen,
  onClose,
  projectId,
  statuses,
  defaultStatusId,
  initialData,
  onSubmit,
  beforeSubmit,
  isSubmitting = false,
  title = 'New issue',
}: IssueFormProps) {
  const { user } = useAuthStore()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const inlineImageInputRef = useRef<HTMLInputElement | null>(null)
  const descriptionEditorRef = useRef<HTMLDivElement | null>(null)
  const descriptionCanvasWrapRef = useRef<HTMLDivElement | null>(null)
  const wasOpenRef = useRef(isOpen)
  const inlineMediaRef = useRef<InlineMediaDraft[]>([])
  const defaultStatus = resolveDefaultStatusId(statuses, defaultStatusId)

  const [form, setForm] = useState(() => createInitialFormState(initialData, defaultStatus))
  const [attachments, setAttachments] = useState<File[]>([])
  const [inlineMedia, setInlineMedia] = useState<InlineMediaDraft[]>([])
  const [showWorkEstimates, setShowWorkEstimates] = useState(() => hasWorkEstimateValues(initialData))
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(() =>
    Boolean(
      initialData?.milestoneId
      || initialData?.componentId
      || initialData?.labels?.length
      || hasWorkEstimateValues(initialData)
    )
  )
  const [assigneePickerOpen, setAssigneePickerOpen] = useState(false)
  const [assigneeSearch, setAssigneeSearch] = useState('')
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [selectedInlineImageToken, setSelectedInlineImageToken] = useState<string | null>(null)
  const [selectedInlineImageHandle, setSelectedInlineImageHandle] = useState<{ left: number; top: number } | null>(null)
  const [, setDescriptionImageNonce] = useState(0)
  const inlineImageResizeRef = useRef<{ token: string; startX: number; startWidth: number } | null>(null)

  const syncDescriptionFromEditor = () => {
    if (!descriptionEditorRef.current) return
    const nextDescription = descriptionEditorRef.current.innerHTML.trim()
    setForm((previous) => ({
      ...previous,
      description: nextDescription === '<p><br></p>' ? '' : nextDescription,
    }))
  }

  const hydrateEditor = (description: string) => {
    if (!descriptionEditorRef.current) return
    descriptionEditorRef.current.innerHTML = descriptionToEditableHtml(description)
    setSelectedInlineImageToken(null)
    setSelectedInlineImageHandle(null)
  }

  const updateSelectedInlineImageStyles = (token: string | null) => {
    if (!descriptionEditorRef.current) return
    descriptionEditorRef.current.querySelectorAll('img').forEach((node) => {
      if (!(node instanceof HTMLImageElement)) return
      const isSelected = token !== null && node.getAttribute('data-inline-token') === token
      node.style.outline = isSelected ? '3px solid #6366f1' : 'none'
      node.style.outlineOffset = isSelected ? '2px' : '0'
      node.style.cursor = 'pointer'
    })
  }

  const updateSelectedInlineImageHandle = () => {
    if (!descriptionEditorRef.current || !descriptionCanvasWrapRef.current || !selectedInlineImageToken) {
      setSelectedInlineImageHandle(null)
      return
    }

    const image = descriptionEditorRef.current.querySelector(`img[data-inline-token="${selectedInlineImageToken}"]`)
    if (!(image instanceof HTMLImageElement)) {
      setSelectedInlineImageHandle(null)
      return
    }

    const wrapperRect = descriptionCanvasWrapRef.current.getBoundingClientRect()
    const imageRect = image.getBoundingClientRect()
    setSelectedInlineImageHandle({
      left: imageRect.right - wrapperRect.left - 8,
      top: imageRect.bottom - wrapperRect.top - 8 + descriptionCanvasWrapRef.current.scrollTop,
    })
  }

  useEffect(() => {
    const openedNow = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen

    if (openedNow) {
      const nextForm = createInitialFormState(initialData, defaultStatus)
      setForm(nextForm)
      setAttachments([])
      setInlineMedia([])
      setShowWorkEstimates(hasWorkEstimateValues(initialData))
      setShowAdvancedOptions(
        Boolean(
          initialData?.milestoneId
          || initialData?.componentId
          || initialData?.labels?.length
          || hasWorkEstimateValues(initialData)
        )
      )
      setSubmitAttempted(false)
      window.requestAnimationFrame(() => hydrateEditor(nextForm.description))
      return
    }

    if (!isOpen) {
      const nextForm = createInitialFormState(initialData, defaultStatus)
      setForm(nextForm)
      setAttachments([])
      setInlineMedia([])
      setShowWorkEstimates(hasWorkEstimateValues(initialData))
      setShowAdvancedOptions(
        Boolean(
          initialData?.milestoneId
          || initialData?.componentId
          || initialData?.labels?.length
          || hasWorkEstimateValues(initialData)
        )
      )
      setSubmitAttempted(false)
      window.requestAnimationFrame(() => hydrateEditor(nextForm.description))
    }
  }, [defaultStatus, initialData, isOpen])

  useEffect(() => {
    if (!isOpen || initialData || form.statusId || !defaultStatus) return

    setForm((previous) => ({
      ...previous,
      statusId: defaultStatus,
    }))
  }, [defaultStatus, form.statusId, initialData, isOpen])

  useEffect(() => {
    if (!isOpen || !initialData) return

    const nextForm = createInitialFormState(initialData, defaultStatus)
    setForm(nextForm)
    setAttachments([])
    setInlineMedia([])
    setShowWorkEstimates(hasWorkEstimateValues(initialData))
    setShowAdvancedOptions(
      Boolean(
        initialData?.milestoneId
        || initialData?.componentId
        || initialData?.labels?.length
        || hasWorkEstimateValues(initialData)
      )
    )
    setSubmitAttempted(false)
    window.requestAnimationFrame(() => hydrateEditor(nextForm.description))
  }, [defaultStatus, initialData?.id, initialData?.updatedAt, isOpen])

  useEffect(() => {
    if (!isOpen) {
      setShowWorkEstimates(false)
    }
  }, [isOpen])

  useEffect(() => {
    inlineMediaRef.current = inlineMedia
  }, [inlineMedia])

  useEffect(() => {
    return () => {
      inlineMediaRef.current.forEach((draft) => URL.revokeObjectURL(draft.previewUrl))
    }
  }, [])

  useEffect(() => {
    updateSelectedInlineImageStyles(selectedInlineImageToken)
    updateSelectedInlineImageHandle()
  }, [selectedInlineImageToken, form.description])

  useEffect(() => {
    const handleWindowResize = () => updateSelectedInlineImageHandle()
    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  })

  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.getProject(projectId),
    enabled: isOpen && !!projectId,
  })

  const { data: membersData } = useQuery({
    queryKey: ['members', projectId],
    queryFn: () => projectsApi.listMembers(projectId),
    enabled: isOpen && !!projectId,
  })

  const { data: labelsData } = useQuery({
    queryKey: ['labels', projectId],
    queryFn: () => projectsApi.listLabels(projectId),
    enabled: isOpen && !!projectId,
  })

  const { data: milestonesData } = useQuery({
    queryKey: ['milestones', projectId],
    queryFn: () => projectsApi.listMilestones(projectId),
    enabled: isOpen && !!projectId,
  })

  const { data: componentsData } = useQuery({
    queryKey: ['components', projectId],
    queryFn: () => projectsApi.listComponents(projectId),
    enabled: isOpen && !!projectId,
  })

  const project = projectData?.data?.data
  const members = membersData?.data?.data || []
  const labels = labelsData?.data?.data || []
  const milestones = milestonesData?.data?.data || []
  const components = componentsData?.data?.data || []

  useEffect(() => {
    if (!isOpen || initialData || form.assigneeIds.length > 0 || members.length === 0) return

    const projectMemberIds = members.map((member) => member.userId)
    const defaultAssigneeIds = projectMemberIds.length > 0
      ? projectMemberIds
      : user?.id
        ? [user.id]
        : []

    if (defaultAssigneeIds.length > 0) {
      setForm((p) => ({
        ...p,
        assigneeIds: defaultAssigneeIds,
        assigneeEstimates: Object.fromEntries(defaultAssigneeIds.map((userId) => [userId, p.assigneeEstimates[userId] || ''])),
        assigneeWorkTypes: Object.fromEntries(defaultAssigneeIds.map((userId) => [userId, p.assigneeWorkTypes[userId] || p.taskWorkType])),
        assigneeDueDates: Object.fromEntries(defaultAssigneeIds.map((userId) => [userId, p.assigneeDueDates[userId] || p.dueDate || ''])),
      }))
    }
  }, [isOpen, initialData, form.assigneeIds.length, user?.id, members])

  const selectedStatus = statuses.find((s) => s.id === form.statusId)
  const selectedPriority = PRIORITY_OPTIONS.find((p) => p.value === form.priority)
  const selectedAssignees = members.filter((m) => form.assigneeIds.includes(m.userId))
  const selectedLabels = labels.filter((l) => form.labelIds.includes(l.id))
  const selectedMilestone = milestones.find((m) => m.id === form.milestoneId)
  const selectedComponent = components.find((c) => c.id === form.componentId)
  const selectedWorkTypeTotal = WORK_TYPE_OPTIONS.reduce((sum, option) => {
    const value = parseFloat(form.workEstimates[option.value] || '0')
    return sum + (Number.isFinite(value) ? value : 0)
  }, 0)
  const assigneeWorkEstimateTotals = WORK_TYPE_OPTIONS.map((option) => ({
    workType: option.value,
    hours: form.assigneeIds.reduce((sum, userId) => {
      if (form.assigneeWorkTypes[userId] !== option.value) return sum
      const value = parseFloat(form.assigneeEstimates[userId] || '0')
      return sum + (Number.isFinite(value) ? value : 0)
    }, 0),
  }))
  const assigneeWorkTypeTotal = assigneeWorkEstimateTotals.reduce((sum, estimate) => sum + estimate.hours, 0)
  const assigneeEstimateTotal = form.assigneeIds.reduce((sum, userId) => {
    const value = parseFloat(form.assigneeEstimates[userId] || '0')
    return sum + (Number.isFinite(value) ? value : 0)
  }, 0)
  const missingRequiredFields = [
    !form.title.trim() ? 'Issue title' : null,
    !form.statusId ? 'Status' : null,
  ].filter((value): value is string => Boolean(value))
  const canSubmit = missingRequiredFields.length === 0

  const syncAssignees = (assigneeIds: string[]) => {
    setForm((previous) => ({
      ...previous,
      assigneeIds,
      assigneeEstimates: Object.fromEntries(
        assigneeIds.map((userId) => [userId, previous.assigneeEstimates[userId] || ''])
      ),
      assigneeWorkTypes: Object.fromEntries(
        assigneeIds.map((userId) => [userId, previous.assigneeWorkTypes[userId] || previous.taskWorkType])
      ),
      assigneeDueDates: Object.fromEntries(
        assigneeIds.map((userId) => [userId, previous.assigneeDueDates[userId] || previous.dueDate || ''])
      ),
    }))
  }

  const setTaskEstimate = (estimatedHours: string, taskWorkType = form.taskWorkType) => {
    setForm((previous) => ({
      ...previous,
      estimatedHours,
      taskWorkType,
      workEstimates: Object.fromEntries(
        WORK_TYPE_OPTIONS.map((option) => [
          option.value,
          option.value === taskWorkType ? estimatedHours : '',
        ])
      ) as typeof previous.workEstimates,
    }))
  }
  const assigneePickerRef = useRef<HTMLDivElement | null>(null)
  const searchText = assigneeSearch.trim().toLowerCase()
  const matchesSearch = (fullName: string, email: string) =>
    !searchText ||
    fullName.toLowerCase().includes(searchText) ||
    email.toLowerCase().includes(searchText)
  const selectedPickerPeople = selectedAssignees.filter((member) => matchesSearch(member.user.fullName, member.user.email))
  const availablePickerPeople = members
    .filter((member) => !form.assigneeIds.includes(member.userId))
    .filter((member) => matchesSearch(member.user.fullName, member.user.email))

  useEffect(() => {
    if (!assigneePickerOpen) return

    const handlePointerDown = (event: MouseEvent) => {
      if (!assigneePickerRef.current?.contains(event.target as Node)) {
        setAssigneePickerOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [assigneePickerOpen])

  const addFiles = (files: FileList | File[] | null) => {
    if (!files?.length) return
    const nextFiles = Array.from(files)
    const rejected = nextFiles.filter((file) => !isAllowedAttachmentFile(file))
    if (rejected.length > 0) {
      window.alert(getAllowedAttachmentMessage())
    }

    setAttachments((current) => [...current, ...nextFiles.filter(isAllowedAttachmentFile)])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const runEditorCommand = (command: string, value?: string) => {
    descriptionEditorRef.current?.focus()
    document.execCommand(command, false, value)
    syncDescriptionFromEditor()
  }

  const insertEditorLink = () => {
    const url = window.prompt('Paste link URL')
    if (!url?.trim()) return
    runEditorCommand('createLink', url.trim())
  }

  const insertInlineMediaDraft = (draft: InlineMediaDraft) => {
    descriptionEditorRef.current?.focus()
    const imageHtml = `<p><img src="${draft.previewUrl}" alt="${draft.label.replace(/"/g, '&quot;')}" data-inline-token="${draft.token}" data-width="480" style="width:480px;max-width:100%;height:auto;display:block;margin:10px 0;border-radius:12px;border:1px solid #dbe3ee;background:#f8fafc;cursor:pointer;" /></p><p><br></p>`
    document.execCommand('insertHTML', false, imageHtml)
    setInlineMedia((current) => [...current, draft])
    setSelectedInlineImageToken(draft.token)
    setDescriptionImageNonce((value) => value + 1)
    syncDescriptionFromEditor()
  }

  const removeSelectedInlineImage = () => {
    if (!descriptionEditorRef.current || !selectedInlineImageToken) return
    const image = descriptionEditorRef.current.querySelector(`img[data-inline-token="${selectedInlineImageToken}"]`)
    if (!(image instanceof HTMLImageElement)) return
    const removedDraft = inlineMediaRef.current.find((draft) => draft.token === selectedInlineImageToken)
    const parent = image.closest('p')
    if (parent?.parentElement) {
      const nextSibling = parent.nextElementSibling
      parent.remove()
      if (nextSibling instanceof HTMLElement && nextSibling.innerHTML === '<br>') {
        nextSibling.remove()
      }
    } else {
      image.remove()
    }
    setInlineMedia((current) => current.filter((draft) => draft.token !== selectedInlineImageToken))
    if (removedDraft) {
      URL.revokeObjectURL(removedDraft.previewUrl)
    }
    setSelectedInlineImageToken(null)
    setSelectedInlineImageHandle(null)
    syncDescriptionFromEditor()
  }

  const queueInlineMediaFiles = (files: FileList | File[] | null) => {
    if (!files?.length) return

    Array.from(files).forEach((file) => {
      const draft = createInlineMediaDraft(file)
      if (draft) {
        insertInlineMediaDraft(draft)
      }
    })

    if (inlineImageInputRef.current) inlineImageInputRef.current.value = ''
  }

  const handlePasteFiles = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const files = Array.from(event.clipboardData.files)
    if (files.length === 0) return
    event.preventDefault()
    const inlineFiles = files.filter(isInlineMediaFile)
    const attachmentFiles = files.filter((file) => !isInlineMediaFile(file))
    if (inlineFiles.length > 0) queueInlineMediaFiles(inlineFiles)
    if (attachmentFiles.length > 0) addFiles(attachmentFiles)
  }

  const handleDropFiles = (event: React.DragEvent<HTMLDivElement>) => {
    const files = Array.from(event.dataTransfer.files)
    if (files.length === 0) return
    event.preventDefault()
    const inlineFiles = files.filter(isInlineMediaFile)
    const attachmentFiles = files.filter((file) => !isInlineMediaFile(file))
    if (inlineFiles.length > 0) queueInlineMediaFiles(inlineFiles)
    if (attachmentFiles.length > 0) addFiles(attachmentFiles)
  }

  const removeAttachment = (index: number) => {
    setAttachments((current) => current.filter((_, i) => i !== index))
  }

  const handleDescriptionEditorClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target
    if (target instanceof HTMLImageElement && target.getAttribute('data-inline-token')) {
      setSelectedInlineImageToken(target.getAttribute('data-inline-token'))
      return
    }
    setSelectedInlineImageToken(null)
  }

  const handleDescriptionEditorKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!selectedInlineImageToken) return
    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault()
      removeSelectedInlineImage()
    }
  }

  const handleInlineImageResizePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!descriptionEditorRef.current || !selectedInlineImageToken) return
    const image = descriptionEditorRef.current.querySelector(`img[data-inline-token="${selectedInlineImageToken}"]`)
    if (!(image instanceof HTMLImageElement)) return

    event.preventDefault()
    event.stopPropagation()
    inlineImageResizeRef.current = {
      token: selectedInlineImageToken,
      startX: event.clientX,
      startWidth: image.getBoundingClientRect().width,
    }

    const handlePointerMove = (moveEvent: PointerEvent) => {
      if (!descriptionEditorRef.current || !inlineImageResizeRef.current) return
      const activeImage = descriptionEditorRef.current.querySelector(`img[data-inline-token="${inlineImageResizeRef.current.token}"]`)
      if (!(activeImage instanceof HTMLImageElement)) return
      const delta = moveEvent.clientX - inlineImageResizeRef.current.startX
      const nextWidth = Math.max(140, Math.min(900, Math.round(inlineImageResizeRef.current.startWidth + delta)))
      activeImage.setAttribute('data-width', String(nextWidth))
      activeImage.style.width = `${nextWidth}px`
      activeImage.style.maxWidth = '100%'
      activeImage.style.height = 'auto'
      updateSelectedInlineImageHandle()
    }

    const handlePointerUp = () => {
      inlineImageResizeRef.current = null
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      syncDescriptionFromEditor()
      updateSelectedInlineImageHandle()
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
  }

  const handleSubmit = () => {
    setSubmitAttempted(true)
    if (!canSubmit) return

    const payload = {
      title: form.title.trim(),
      description: form.description || undefined,
      statusId: form.statusId,
      priority: form.priority,
      assigneeIds: form.assigneeIds.length > 0 ? form.assigneeIds : undefined,
      assigneeEstimates: form.assigneeIds.map((userId) => {
        const estimate = parseFloat(form.assigneeEstimates[userId] || '')
        return {
          userId,
          estimatedHours: Number.isFinite(estimate) ? estimate : null,
          workType: form.assigneeWorkTypes[userId] || null,
          dueDate: form.assigneeDueDates[userId] ? new Date(form.assigneeDueDates[userId]).toISOString() : null,
        }
      }),
      workEstimates: assigneeWorkTypeTotal > 0
        ? assigneeWorkEstimateTotals
        : WORK_TYPE_OPTIONS.map((option) => {
            const value = parseFloat(form.workEstimates[option.value] || '')
            return {
              workType: option.value,
              hours: Number.isFinite(value) ? value : 0,
            }
          }),
      labelIds: form.labelIds.length > 0 ? form.labelIds : undefined,
      milestoneId: form.milestoneId || undefined,
      componentId: form.componentId || undefined,
      estimatedHours: assigneeEstimateTotal > 0
        ? assigneeEstimateTotal
        : selectedWorkTypeTotal > 0
        ? selectedWorkTypeTotal
        : form.estimatedHours
          ? parseFloat(form.estimatedHours)
          : undefined,
      startDate: form.startDate ? new Date(form.startDate).toISOString() : undefined,
      dueDate: form.dueDate ? new Date(form.dueDate).toISOString() : undefined,
      attachments,
      inlineMedia,
    }

    if (beforeSubmit && !beforeSubmit(payload)) {
      return
    }

    onSubmit(payload)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} width={860} closeOnEscape={false}>
      <div style={{ margin: '-22px', color: '#1f2937' }}>
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span style={{ ...crumbStyle, color: '#4b5563' }}>
              <span style={{ color: '#ec4899' }}>◎</span>
              <span style={ellipsisStyle}>{project?.name || 'Project'}</span>
            </span>
            <span style={crumbStyle}>No template</span>
            <ChevronRight size={14} style={{ color: '#9ca3af', flexShrink: 0 }} />
            <span style={{ fontSize: 14, color: '#111827', whiteSpace: 'nowrap' }}>{title}</span>
          </div>
          <button onClick={onClose} style={iconButtonStyle} title="Close">
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '10px 24px 18px' }}>
          <div style={titleFieldWrapStyle}>
            <input
              autoFocus
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              placeholder="Issue title"
              style={{
                ...titleFieldInputStyle,
                borderBottomColor: submitAttempted && !form.title.trim() ? '#ef4444' : titleFieldInputStyle.borderBottomColor,
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSubmit()
                }
              }}
            />
          </div>

          <div style={descriptionEditorStyle}>
            <div style={descriptionToolbarStyle}>
              <div style={compactToolbarGroupStyle}>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => runEditorCommand('bold')}
                  style={descriptionToolbarButtonStyle}
                  title="Bold"
                >
                  <Bold size={15} />
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => runEditorCommand('italic')}
                  style={descriptionToolbarButtonStyle}
                  title="Italic"
                >
                  <Italic size={15} />
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => runEditorCommand('underline')}
                  style={descriptionToolbarButtonStyle}
                  title="Underline"
                >
                  <Underline size={15} />
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => runEditorCommand('strikeThrough')}
                  style={descriptionToolbarButtonStyle}
                  title="Strikethrough"
                >
                  <Strikethrough size={15} />
                </button>
                <span style={toolbarDividerStyle} />
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => runEditorCommand('insertUnorderedList')}
                  style={descriptionToolbarButtonStyle}
                  title="Bullet list"
                >
                  <List size={15} />
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => runEditorCommand('insertOrderedList')}
                  style={descriptionToolbarButtonStyle}
                  title="Numbered list"
                >
                  <ListOrdered size={15} />
                </button>
                <span style={toolbarDividerStyle} />
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={insertEditorLink}
                  style={descriptionToolbarButtonStyle}
                  title="Insert link"
                >
                  <Link2 size={15} />
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => inlineImageInputRef.current?.click()}
                  style={descriptionToolbarButtonStyle}
                  title="Insert image inside the description"
                >
                  <ImagePlus size={14} />
                </button>
              </div>
              {selectedInlineImageToken && (
                <div style={inlineImageControlsStyle}>
                  <button type="button" style={inlineImageDeleteButtonStyle} onClick={removeSelectedInlineImage}>Delete image</button>
                </div>
              )}
            </div>
            <div
              ref={descriptionCanvasWrapRef}
              style={descriptionCanvasWrapStyle}
              onScroll={updateSelectedInlineImageHandle}
            >
              {!form.description.trim() && (
                <div style={descriptionPlaceholderStyle}>Add description, acceptance criteria, notes, screenshots...</div>
              )}
              <div
                ref={descriptionEditorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={syncDescriptionFromEditor}
                onBlur={syncDescriptionFromEditor}
                onPaste={handlePasteFiles}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDropFiles}
                onClick={handleDescriptionEditorClick}
                onKeyDown={handleDescriptionEditorKeyDown}
                style={descriptionEditorCanvasStyle}
              />
              {selectedInlineImageHandle && (
                <button
                  type="button"
                  onPointerDown={handleInlineImageResizePointerDown}
                  style={{
                    ...inlineImageResizeHandleStyle,
                    left: selectedInlineImageHandle.left,
                    top: selectedInlineImageHandle.top,
                  }}
                  title="Drag to resize image"
                />
              )}
            </div>
            <input
              ref={inlineImageInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(event) => queueInlineMediaFiles(event.target.files)}
              style={{ display: 'none' }}
            />
          </div>

          <div style={compactFieldGridStyle}>
            <FormField label="Status *">
              <select
                value={form.statusId}
                onChange={(e) => setForm((p) => ({ ...p, statusId: e.target.value }))}
                style={fieldInputStyle}
              >
                {statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </FormField>

            <FormField label="Priority">
              <select
                value={form.priority}
                onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value as Issue['priority'] }))}
                style={fieldInputStyle}
              >
                {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </FormField>

            <FormField label="Overall due date">
              <input
                type="date"
                value={form.dueDate}
                onChange={(e) => setForm((p) => ({ ...p, dueDate: e.target.value }))}
                style={fieldInputStyle}
              />
            </FormField>

            <FormField label="Task type">
              <select
                value={form.taskWorkType}
                onChange={(event) => setTaskEstimate(form.estimatedHours, event.target.value as typeof form.taskWorkType)}
                style={fieldInputStyle}
              >
                {WORK_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Overall estimate">
              <input
                type="number"
                min="0"
                step="0.5"
                value={form.estimatedHours}
                onChange={(event) => setTaskEstimate(event.target.value)}
                placeholder="Hours"
                style={fieldInputStyle}
              />
            </FormField>
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={teamPlanHeaderStyle}>
              <label style={fieldLabelStyle}>Assignees *</label>
              <span style={teamPlanSummaryStyle}>
                {selectedAssignees.length} selected
                {assigneeEstimateTotal > 0 ? ` · ${assigneeEstimateTotal}h assigned` : ''}
              </span>
            </div>
            <div ref={assigneePickerRef} style={assigneePickerWrapStyle}>
              <button
                type="button"
                onClick={() => setAssigneePickerOpen((open) => !open)}
                style={assigneePickerButtonStyle}
              >
                {selectedAssignees.length > 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {selectedAssignees.map((assignee) => (
                      <span key={assignee.userId} style={selectedPersonChipStyle}>
                        <span style={selectedPersonAvatarStyle}>{initialsOf(assignee.user.fullName)}</span>
                        <span style={ellipsisStyle}>{assignee.user.fullName}</span>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            syncAssignees(form.assigneeIds.filter((id) => id !== assignee.userId))
                          }}
                          style={chipRemoveButtonStyle}
                          title={`Remove ${assignee.user.fullName}`}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <span style={{ color: '#9ca3af' }}>Select assignees</span>
                )}
              </button>

              {assigneePickerOpen && (
                <div style={assigneePopoverStyle}>
                  <div style={assigneeSearchStyle}>
                    <Search size={15} />
                    <input
                      autoFocus
                      value={assigneeSearch}
                      onChange={(event) => setAssigneeSearch(event.target.value)}
                      placeholder="Search people..."
                      style={assigneeSearchInputStyle}
                    />
                  </div>

                  {selectedPickerPeople.length > 0 && (
                    <>
                      <div style={pickerSectionLabelStyle}>Selected</div>
                      {selectedPickerPeople.map((assignee) => (
                        <PickerRow
                          key={assignee.userId}
                          label={assignee.user.fullName}
                          selected
                          onClick={() => syncAssignees(form.assigneeIds.filter((id) => id !== assignee.userId))}
                        />
                      ))}
                    </>
                  )}

                  <div style={pickerSectionLabelStyle}>People</div>
                  {availablePickerPeople.length > 0 ? availablePickerPeople.map((assignee) => (
                    <PickerRow
                      key={assignee.userId}
                      label={assignee.user.fullName}
                      selected={false}
                      onClick={() => syncAssignees([...form.assigneeIds, assignee.userId])}
                    />
                  )) : (
                    <div style={pickerEmptyStyle}>No people found</div>
                  )}
                </div>
              )}
            </div>

            {selectedAssignees.length > 0 && (
              <div style={teamPlanStyle}>
                {selectedAssignees.map((assignee) => (
                  <div key={assignee.userId} style={teamPlanRowStyle}>
                    <div style={teamPersonStyle}>
                      <span style={teamPersonAvatarStyle}>{initialsOf(assignee.user.fullName)}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={teamPersonNameStyle}>{assignee.user.fullName}</div>
                        <div style={teamPersonMetaStyle}>{assignee.user.email}</div>
                      </div>
                    </div>
                    <label style={teamFieldStyle}>
                      <span style={teamEstimateLabelStyle}>Type</span>
                      <select
                        value={form.assigneeWorkTypes[assignee.userId] || ''}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            assigneeWorkTypes: {
                              ...previous.assigneeWorkTypes,
                              [assignee.userId]: event.target.value as typeof form.taskWorkType,
                            },
                          }))
                        }
                        style={teamSelectStyle}
                      >
                        <option value="">Work type</option>
                        {WORK_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <div style={teamEstimateStyle}>
                      <span style={teamEstimateLabelStyle}>Time</span>
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={form.assigneeEstimates[assignee.userId] || ''}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            assigneeEstimates: {
                              ...previous.assigneeEstimates,
                              [assignee.userId]: event.target.value,
                            },
                          }))
                        }
                        placeholder="0"
                        style={teamEstimateInputStyle}
                      />
                      <span style={teamEstimateUnitStyle}>h</span>
                    </div>
                    <label style={teamFieldStyle}>
                      <span style={teamEstimateLabelStyle}>Due date</span>
                      <input
                        type="date"
                        value={form.assigneeDueDates[assignee.userId] || ''}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            assigneeDueDates: {
                              ...previous.assigneeDueDates,
                              [assignee.userId]: event.target.value,
                            },
                          }))
                        }
                        style={teamDateInputStyle}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => syncAssignees(form.assigneeIds.filter((id) => id !== assignee.userId))}
                      style={teamRemoveButtonStyle}
                      title={`Remove ${assignee.user.fullName}`}
                    >
                      <X size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={advancedBoxStyle}>
            <button
              type="button"
              onClick={() => setShowAdvancedOptions((value) => !value)}
              style={advancedToggleButtonStyle}
            >
              <span>More options</span>
              <span style={{ color: '#6b7280', fontSize: 12 }}>{showAdvancedOptions ? 'Hide' : 'Show'}</span>
            </button>

            {showAdvancedOptions && (
              <>
                <div style={{ ...fieldGridStyle, marginTop: 14 }}>
                  <FormField label="Milestone">
                    <select
                      value={form.milestoneId}
                      onChange={(e) => setForm((p) => ({ ...p, milestoneId: e.target.value }))}
                      style={fieldInputStyle}
                    >
                      <option value="">Milestone</option>
                      {milestones.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </FormField>

                  <FormField label="Component">
                    <select
                      value={form.componentId}
                      onChange={(e) => setForm((p) => ({ ...p, componentId: e.target.value }))}
                      style={fieldInputStyle}
                    >
                      <option value="">No component</option>
                      {components.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </FormField>
                </div>

          <div style={{ marginTop: 14 }}>
            <label style={fieldLabelStyle}>Labels</label>
            <div style={labelsBoxStyle}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', minHeight: 32 }}>
                {selectedLabels.length > 0 ? selectedLabels.map((label) => (
                  <span key={label.id} style={selectedLabelChipStyle}>
                    {label.name}
                    <button
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, labelIds: p.labelIds.filter((id) => id !== label.id) }))}
                      style={chipRemoveButtonStyle}
                      title={`Remove ${label.name}`}
                    >
                      <X size={12} />
                    </button>
                  </span>
                )) : <span style={{ color: '#9ca3af', fontSize: 13 }}>No labels selected</span>}
              </div>
              <select
                value=""
                onChange={(event) => {
                  if (!event.target.value) return
                  setForm((p) => ({
                    ...p,
                    labelIds: Array.from(new Set([...p.labelIds, event.target.value])),
                  }))
                }}
                style={{ ...fieldInputStyle, marginTop: 10 }}
              >
                <option value="">Select label</option>
                {labels.filter((label) => !form.labelIds.includes(label.id)).map((label) => (
                  <option key={label.id} value={label.id}>{label.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={workEstimateBoxStyle}>
            <div
              style={{ ...workEstimateHeaderStyle, cursor: 'pointer', userSelect: 'none' }}
              onClick={() => setShowWorkEstimates((v) => !v)}
            >
              <span>Estimated by work type</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {selectedWorkTypeTotal > 0 && <span>{selectedWorkTypeTotal}h total</span>}
                <span style={{ fontSize: 11, color: '#6b7280' }}>{showWorkEstimates ? '▲' : '▼'}</span>
              </span>
            </div>
            {showWorkEstimates && (
              <div style={workEstimateGridStyle}>
                {WORK_TYPE_OPTIONS.map((option) => (
                  <label key={option.value} style={workEstimateRowStyle}>
                    <span style={ellipsisStyle}>{formatWorkType(option.value)}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={form.workEstimates[option.value] || ''}
                      onChange={(event) =>
                        setForm((previous) => ({
                          ...previous,
                          workEstimates: {
                            ...previous.workEstimates,
                            [option.value]: event.target.value,
                          },
                        }))
                      }
                      placeholder="0"
                      style={estimateInputStyle}
                    />
                  </label>
                ))}
              </div>
            )}
          </div>
              </>
            )}
          </div>
        </div>

        {attachments.length > 0 && (
          <div style={{ padding: '0 24px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {attachments.map((file, index) => (
              <div key={`${file.name}-${index}`} style={attachmentRowStyle}>
                <FileThumb file={file} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: '#1f2937', ...ellipsisStyle }}>{file.name}</div>
                  <div style={{ fontSize: 11, color: '#8a93a3' }}>{(file.size / 1024).toFixed(1)} KB</div>
                </div>
                <button onClick={() => removeAttachment(index)} style={iconButtonStyle} title="Remove file">
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={footerStyle}>
          <div style={footerLeftStyle}>
            <input ref={fileInputRef} type="file" multiple accept={ALLOWED_ATTACHMENT_ACCEPT} onChange={(e) => addFiles(e.target.files)} style={{ display: 'none' }} />
            <button onClick={() => fileInputRef.current?.click()} style={iconButtonStyle} title="Attach files">
              <Paperclip size={18} />
            </button>
            {submitAttempted && !canSubmit && (
              <span style={validationMessageStyle}>
                Required: {missingRequiredFields.join(', ')}
              </span>
            )}
          </div>
          <Button
            variant="primary"
            loading={isSubmitting}
            disabled={!canSubmit}
            onClick={handleSubmit}
            style={{
              height: 36,
              padding: '0 18px',
              textTransform: 'lowercase',
              background: canSubmit ? '#6366f1' : '#d4d4d8',
              color: canSubmit ? '#ffffff' : '#71717a',
            }}
          >
            {initialData ? 'save changes' : 'create issue'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function FileThumb({ file }: { file: File }) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!file.type.startsWith('image/')) {
      setPreviewUrl(null)
      return
    }

    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  if (previewUrl) {
    return <img src={previewUrl} alt={file.name} style={fileThumbStyle} />
  }

  return (
    <span style={fileThumbStyle}>
      <Paperclip size={14} />
    </span>
  )
}

function PickerRow({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={{ ...pickerRowStyle, background: selected ? '#eef2ff' : '#ffffff' }}>
      <span style={{ ...ellipsisStyle, flex: 1, textAlign: 'left' }}>{label}</span>
      {selected && <Check size={14} style={{ color: '#4f46e5', flexShrink: 0 }} />}
    </button>
  )
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={fieldLabelStyle}>{label}</span>
      {children}
    </label>
  )
}

function initialsOf(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('')
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '18px 24px 8px',
}

const crumbStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  height: 26,
  padding: '0 10px',
  border: '1px solid #dfe3ea',
  borderRadius: 6,
  background: '#ffffff',
  color: '#6b7280',
  fontSize: 12,
  maxWidth: 190,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}

const plainFieldStyle: React.CSSProperties = {
  width: '100%',
  padding: 0,
  fontSize: 14,
  border: 'none',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  background: 'transparent',
}

const titleFieldWrapStyle: React.CSSProperties = {
  marginBottom: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
}

const titleFieldInputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 52,
  padding: '10px 4px 12px',
  fontSize: 22,
  lineHeight: '28px',
  border: 'none',
  borderBottom: '1px solid #e5e7eb',
  borderBottomColor: '#e5e7eb',
  borderRadius: 0,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  color: '#111827',
  background: '#ffffff',
}

const fieldGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 14,
}

const compactFieldGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 12,
}

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
}

const fieldInputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 40,
  border: '1px solid #d7dce3',
  borderRadius: 10,
  padding: '0 12px',
  fontSize: 13,
  color: '#111827',
  background: '#ffffff',
  outline: 'none',
  boxSizing: 'border-box',
}

const advancedBoxStyle: React.CSSProperties = {
  marginTop: 14,
  borderTop: '1px solid #eef2f7',
  paddingTop: 14,
}

const advancedToggleButtonStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 40,
  border: '1px solid #d7dce3',
  borderRadius: 10,
  background: '#fbfcfe',
  color: '#334155',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 12px',
  fontSize: 13,
  fontWeight: 600,
}

const advancedContentStyle: React.CSSProperties = {
  marginTop: 14,
}

const iconButtonStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  border: 'none',
  background: 'transparent',
  color: '#6b7280',
  cursor: 'pointer',
  borderRadius: 6,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const attachmentRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  border: '1px solid #dfe3ea',
  borderRadius: 6,
  padding: '7px 9px',
  background: '#fafafa',
}

const workEstimateBoxStyle: React.CSSProperties = {
  marginTop: 12,
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  padding: '12px',
  background: '#fbfcfe',
}

const workEstimateHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  fontSize: 12.5,
  color: '#4b5563',
  marginBottom: 10,
}

const workEstimateGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
  gap: 8,
}

const workEstimateRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 82px',
  gap: 8,
  alignItems: 'center',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  padding: '7px 9px',
  fontSize: 12.5,
  color: '#4b5563',
  background: '#ffffff',
}

const estimateInputStyle: React.CSSProperties = {
  width: '100%',
  height: 28,
  border: '1px solid #dfe3ea',
  borderRadius: 5,
  padding: '0 8px',
  fontSize: 12.5,
  outline: 'none',
  boxSizing: 'border-box',
}

const fileThumbStyle: React.CSSProperties = {
  width: 34,
  height: 28,
  border: '1px solid #d1d5db',
  borderRadius: 4,
  background: '#f3f4f6',
  color: '#667085',
  objectFit: 'cover',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}

const footerStyle: React.CSSProperties = {
  borderTop: '1px solid #e5e7eb',
  padding: '13px 24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}

const footerLeftStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minWidth: 0,
}

const validationMessageStyle: React.CSSProperties = {
  fontSize: 12,
  lineHeight: '18px',
  color: '#dc2626',
  whiteSpace: 'normal',
}

const ellipsisStyle: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const assigneePickerWrapStyle: React.CSSProperties = {
  position: 'relative',
}

const assigneePickerButtonStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 44,
  border: '1px solid #d7dce3',
  borderRadius: 10,
  background: '#ffffff',
  cursor: 'pointer',
  padding: '10px 12px',
  textAlign: 'left',
}

const teamPlanHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 6,
}

const teamPlanSummaryStyle: React.CSSProperties = {
  color: '#6b7280',
  fontSize: 12,
  whiteSpace: 'nowrap',
}

const teamPlanStyle: React.CSSProperties = {
  marginTop: 10,
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  background: '#fbfcfe',
  overflow: 'hidden',
}

const teamPlanRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(170px, 1fr) minmax(160px, 190px) 132px minmax(140px, 160px) 34px',
  alignItems: 'center',
  gap: 10,
  minHeight: 70,
  padding: '9px 10px',
  borderBottom: '1px solid #edf1f6',
}

const teamPersonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minWidth: 0,
}

const teamPersonAvatarStyle: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: '50%',
  background: '#111827',
  color: '#ffffff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 11,
  fontWeight: 700,
  flexShrink: 0,
}

const teamPersonNameStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: '#111827',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const teamPersonMetaStyle: React.CSSProperties = {
  marginTop: 2,
  fontSize: 11.5,
  color: '#6b7280',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const teamEstimateStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
  alignItems: 'center',
  gap: 6,
}

const teamFieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  minWidth: 0,
}

const teamEstimateLabelStyle: React.CSSProperties = {
  fontSize: 11.5,
  color: '#6b7280',
}

const teamSelectStyle: React.CSSProperties = {
  width: '100%',
  height: 32,
  border: '1px solid #d7dce3',
  borderRadius: 8,
  padding: '0 8px',
  fontSize: 12.5,
  color: '#111827',
  background: '#ffffff',
  outline: 'none',
  boxSizing: 'border-box',
}

const teamEstimateInputStyle: React.CSSProperties = {
  width: '100%',
  height: 32,
  border: '1px solid #d7dce3',
  borderRadius: 8,
  padding: '0 8px',
  fontSize: 13,
  color: '#111827',
  background: '#ffffff',
  outline: 'none',
  boxSizing: 'border-box',
}

const teamDateInputStyle: React.CSSProperties = {
  ...teamEstimateInputStyle,
  fontSize: 12,
}

const teamEstimateUnitStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#6b7280',
}

const teamRemoveButtonStyle: React.CSSProperties = {
  width: 30,
  height: 30,
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  background: '#ffffff',
  color: '#6b7280',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const assigneePopoverStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  right: 0,
  zIndex: 20,
  border: '1px solid #d7dce3',
  borderRadius: 10,
  background: '#ffffff',
  boxShadow: '0 14px 30px rgba(15, 23, 42, 0.14)',
  padding: 8,
  maxHeight: 280,
  overflowY: 'auto',
}

const assigneeSearchStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  border: '1px solid #dbe3ee',
  borderRadius: 8,
  padding: '0 10px',
  minHeight: 36,
  marginBottom: 8,
  color: '#6b7280',
}

const assigneeSearchInputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  fontSize: 13,
  color: '#111827',
  fontFamily: 'inherit',
}

const pickerSectionLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: '#6b7280',
  textTransform: 'uppercase',
  margin: '8px 6px 5px',
}

const pickerRowStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 36,
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '0 10px',
  color: '#111827',
  fontSize: 13,
}

const pickerEmptyStyle: React.CSSProperties = {
  padding: '8px 10px 10px',
  color: '#9ca3af',
  fontSize: 13,
}

const selectedPersonChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  maxWidth: '100%',
  border: '1px solid #dbe3ee',
  borderRadius: 999,
  background: '#f8fafc',
  padding: '4px 8px',
  fontSize: 12.5,
  color: '#374151',
}

const selectedPersonAvatarStyle: React.CSSProperties = {
  width: 20,
  height: 20,
  borderRadius: '50%',
  background: '#6366f1',
  color: '#ffffff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 10,
  fontWeight: 700,
  flexShrink: 0,
}

const selectedLabelChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  border: '1px solid #dbe3ee',
  borderRadius: 999,
  background: '#f8fafc',
  padding: '4px 8px',
  fontSize: 12.5,
  color: '#374151',
}

const chipRemoveButtonStyle: React.CSSProperties = {
  width: 16,
  height: 16,
  border: 'none',
  background: 'transparent',
  color: '#6b7280',
  cursor: 'pointer',
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}

const labelsBoxStyle: React.CSSProperties = {
  border: '1px solid #d7dce3',
  borderRadius: 10,
  background: '#ffffff',
  padding: 12,
}

const descriptionEditorStyle: React.CSSProperties = {
  marginBottom: 14,
  border: '1px solid #d7dce3',
  borderRadius: 12,
  background: '#ffffff',
  overflow: 'hidden',
}

const descriptionToolbarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '8px 10px',
  flexWrap: 'wrap',
}

const compactToolbarGroupStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  border: '1px solid #b9c1cb',
  borderRadius: 4,
  background: '#ffffff',
  padding: '2px',
}

const descriptionToolbarButtonStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  border: 'none',
  borderRadius: 4,
  background: 'transparent',
  color: '#334155',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
}

const toolbarDividerStyle: React.CSSProperties = {
  width: 1,
  height: 18,
  background: '#dbe3ee',
  margin: '0 2px',
}

const inlineImageControlsStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  marginLeft: 'auto',
}

const inlineImageDeleteButtonStyle: React.CSSProperties = {
  height: 28,
  border: '1px solid #fecaca',
  borderRadius: 6,
  background: '#fff1f2',
  color: '#dc2626',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  padding: '0 8px',
}

const descriptionCanvasWrapStyle: React.CSSProperties = {
  position: 'relative',
  minHeight: 220,
  resize: 'vertical',
  overflow: 'auto',
  borderTop: '1px solid #e5e7eb',
}

const inlineImageResizeHandleStyle: React.CSSProperties = {
  position: 'absolute',
  width: 16,
  height: 16,
  border: '2px solid #ffffff',
  borderRadius: '50%',
  background: '#6366f1',
  boxShadow: '0 2px 10px rgba(99, 102, 241, 0.35)',
  cursor: 'nwse-resize',
  transform: 'translate(-50%, -50%)',
  zIndex: 2,
}

const descriptionEditorCanvasStyle: React.CSSProperties = {
  minHeight: 220,
  padding: '12px 14px',
  color: '#334155',
  fontSize: 14,
  lineHeight: 1.5,
  outline: 'none',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
}

const descriptionPlaceholderStyle: React.CSSProperties = {
  position: 'absolute',
  top: 14,
  left: 14,
  color: '#94a3b8',
  fontSize: 14,
  pointerEvents: 'none',
}
